import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { subDays } from 'date-fns'
import {
    ExclamationCircleIcon,
    ViewColumnsIcon,
    PencilIcon,
    LockClosedIcon,
    CheckCircleIcon,
    CheckIcon,
    DocumentTextIcon,
    Bars3Icon,
    ArrowTrendingDownIcon,
    ArrowTurnRightDownIcon,
    ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'
import { getInputStyles } from '../../../../../components/Form/styles'
import Button from '../../../../../components/Form/Button'
import {
    equalSplit,
    frontLoadedSplit,
    graduatedDecreasingSplit,
    redistributeAcrossSlots,
    ROUND_TO_OPTIONS,
    suggestRoundTo,
    isRoundToRelevant,
    type RoundToOption,
} from './installmentMath'
import { addMonthsClamped, parseIsoDateLocal } from './installmentDates'

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)

export type LockedInstallmentStatus = 'Paid' | 'Billed' | 'Applied' | 'Scheduled'

export interface InstallmentConfiguratorProps {
    totalAmount: number
    count: number
    /** ISO date string (YYYY-MM-DD) of the first installment's due date. Per-installment
     *  due dates are projected as firstDueDate + i months (with end-of-month clamping);
     *  billDate is shown as dueDate − 14 days. When absent, per-installment date hints
     *  are hidden. */
    firstDueDate?: string
    freeform?: boolean
    lockedStatuses?: LockedInstallmentStatus[]
    lockedAmounts?: number[]
    installments: number[]
    onInstallmentsChange: (installments: number[]) => void
    editedIndices: Set<number>
    onEditedIndicesChange: (indices: Set<number>) => void
    totalLabel?: string
}

export function InstallmentConfigurator({
    totalAmount,
    count,
    firstDueDate,
    freeform = false,
    lockedStatuses,
    lockedAmounts,
    installments,
    onInstallmentsChange,
    editedIndices,
    onEditedIndicesChange,
    totalLabel = 'Plan balance',
}: InstallmentConfiguratorProps) {
    const [roundTo, setRoundTo] = useState<RoundToOption>(10)

    const total = totalAmount
    const effectiveLockedCount = Math.min(Math.max(0, lockedStatuses?.length || 0), count)
    const effectiveStatuses = (lockedStatuses || []).slice(0, effectiveLockedCount)

    const paidOrAppliedCount = effectiveStatuses.filter((s) => s === 'Paid' || s === 'Applied').length
    const billedOrScheduledCount = effectiveStatuses.filter((s) => s === 'Billed' || s === 'Scheduled').length

    const frozenLocked = useMemo(
        () => (lockedAmounts || []).slice(0, effectiveLockedCount).map((a) => parseFloat(a as unknown as string) || 0),
        [lockedAmounts, effectiveLockedCount]
    )

    const frozenLockedSum = useMemo(
        () => frozenLocked.reduce((s, a) => s + a, 0),
        [frozenLocked]
    )

    const buildWithLocked = useCallback(
        (editableArr: number[]) => [...frozenLocked, ...editableArr],
        [frozenLocked]
    )

    // billingDates: per-installment { billDate, dueDate } projected from the first installment
    // due date by adding i months (with end-of-month clamping). billDate ≈ dueDate − 14 days.
    // We anchor each offset on the original first-due date rather than chaining month-by-month,
    // so day-of-month cadence is preserved (e.g. Jan 31 → Jan 31, Feb 28, Mar 31, Apr 30).
    const billingDates = useMemo(() => {
        const first = parseIsoDateLocal(firstDueDate)
        if (first == null || count <= 0) return []
        return Array.from({ length: count }, (_, i) => {
            const due = addMonthsClamped(first, i)
            return { due, bill: subDays(due, 14) }
        })
    }, [firstDueDate, count])

    const prevCountRef = useRef<number | null>(null)
    const prevRoundToRef = useRef<RoundToOption | null>(null)

    const editableTotalForSuggestion = Math.max(0, total - frozenLockedSum)
    const editableCountForSuggestion = Math.max(0, count - effectiveLockedCount)

    useEffect(() => {
        if (editableCountForSuggestion > 0 && editableTotalForSuggestion > 0) {
            setRoundTo(suggestRoundTo(editableTotalForSuggestion, editableCountForSuggestion))
        }
    }, [editableTotalForSuggestion, editableCountForSuggestion])

    useEffect(() => {
        if (freeform) {
            if (count > 0 && prevCountRef.current !== null && prevCountRef.current !== count) {
                const next = Array(count).fill(0)
                for (let i = 0; i < Math.min(effectiveLockedCount, count); i++) {
                    next[i] = frozenLocked[i] || 0
                }
                onInstallmentsChange(next)
                onEditedIndicesChange(new Set())
            }
            if (count > 0) prevCountRef.current = count
            return
        }
        if (count > 0) {
            if (prevCountRef.current === null) {
                if (installments.length === 0) {
                    const editableTotal = Math.max(0, total - frozenLockedSum)
                    const editableCount = Math.max(0, count - effectiveLockedCount)
                    onInstallmentsChange(buildWithLocked(equalSplit(editableTotal, editableCount, roundTo)))
                }
            } else if (prevCountRef.current !== count) {
                const lockedSlice = frozenLocked.slice(0, Math.min(effectiveLockedCount, count))
                const lockedSliceSum = lockedSlice.reduce((s, a) => s + a, 0)
                const editableTotal = Math.max(0, total - lockedSliceSum)
                const editableCount = Math.max(0, count - lockedSlice.length)
                onInstallmentsChange([...lockedSlice, ...equalSplit(editableTotal, editableCount, roundTo)])
                onEditedIndicesChange(new Set())
            }
            prevCountRef.current = count
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, roundTo])

    useEffect(() => {
        if (freeform) return
        if (count > 0 && editedIndices.size === 0 && prevCountRef.current !== null) {
            const editableTotal = Math.max(0, total - frozenLockedSum)
            const editableCount = Math.max(0, count - effectiveLockedCount)
            onInstallmentsChange(buildWithLocked(equalSplit(editableTotal, editableCount, roundTo)))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total, editedIndices.size, roundTo])

    // When roundTo changes with manually edited values, re-distribute the free slots
    useEffect(() => {
        if (prevRoundToRef.current === roundTo) {
            prevRoundToRef.current = roundTo
            return
        }
        prevRoundToRef.current = roundTo

        if (freeform || count <= 0 || prevCountRef.current === null) return
        if (editedIndices.size === 0) return

        const freeSlots: number[] = []
        for (let i = effectiveLockedCount; i < count; i++) {
            if (!editedIndices.has(i)) freeSlots.push(i)
        }
        if (freeSlots.length === 0) return

        const editedSum = Array.from(editedIndices).reduce((s, i) => {
            return i >= effectiveLockedCount && i < installments.length
                ? s + installments[i]
                : s
        }, 0)
        const freeRemaining = Math.round((total - frozenLockedSum - editedSum) * 100) / 100
        onInstallmentsChange(
            redistributeAcrossSlots([...installments], freeSlots, freeRemaining, roundTo)
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roundTo])

    const updateSingleInstallment = useCallback(
        (index: number, value: string) => {
            if (index < effectiveLockedCount) return
            const next = [...installments]
            next[index] = parseFloat(value) || 0
            onInstallmentsChange(next)
            onEditedIndicesChange(new Set(editedIndices).add(index))
        },
        [installments, onInstallmentsChange, editedIndices, onEditedIndicesChange, effectiveLockedCount]
    )

    const installmentsTotal = useMemo(
        () => installments.reduce((s, a) => s + a, 0),
        [installments]
    )

    const difference = useMemo(
        () => Math.round((installmentsTotal - total) * 100) / 100,
        [installmentsTotal, total]
    )

    const hasError = useMemo(
        () => !freeform && total > 0 && Math.abs(difference) >= 0.01,
        [freeform, total, difference]
    )

    const hasZeroInstallment = useMemo(() => {
        const refTotal = freeform ? installments.reduce((s, a) => s + a, 0) : total
        return refTotal > 0 && installments.some((v, i) => i >= effectiveLockedCount && v <= 0)
    }, [freeform, total, installments, effectiveLockedCount])

    const editedOnlyTotal = useMemo(() => {
        let sum = 0
        editedIndices.forEach((i) => {
            if (i >= effectiveLockedCount && i < installments.length) {
                sum += installments[i]
            }
        })
        return sum
    }, [editedIndices, effectiveLockedCount, installments])

    const fixedTotal = useMemo(() => frozenLockedSum + editedOnlyTotal, [frozenLockedSum, editedOnlyTotal])

    const freeCount = useMemo(() => {
        let free = 0
        for (let i = effectiveLockedCount; i < count; i++) {
            if (!editedIndices.has(i)) free++
        }
        return free
    }, [effectiveLockedCount, count, editedIndices])

    const remaining = Math.round((total - fixedTotal) * 100) / 100

    const resetToEqual = useCallback(() => {
        if (count > 0) {
            const editableTotal = Math.max(0, total - frozenLockedSum)
            const editableCount = Math.max(0, count - effectiveLockedCount)
            onInstallmentsChange(buildWithLocked(equalSplit(editableTotal, editableCount, roundTo)))
            onEditedIndicesChange(new Set())
        }
    }, [count, total, frozenLockedSum, effectiveLockedCount, roundTo, onInstallmentsChange, onEditedIndicesChange, buildWithLocked])

    const resetSingleInstallment = useCallback(
        (index: number) => {
            if (index < effectiveLockedCount) return
            const newEditedIndices = new Set(editedIndices)
            newEditedIndices.delete(index)
            onEditedIndicesChange(newEditedIndices)

            if (newEditedIndices.size === 0) return

            const newEditedOnlyTotal = Array.from(newEditedIndices).reduce((s, i) => {
                return i >= effectiveLockedCount && i < installments.length
                    ? s + installments[i]
                    : s
            }, 0)
            const newRemaining = Math.round((total - frozenLockedSum - newEditedOnlyTotal) * 100) / 100
            const freeSlots: number[] = []
            for (let i = effectiveLockedCount; i < count; i++) {
                if (!newEditedIndices.has(i)) freeSlots.push(i)
            }
            const next = [...installments]
            onInstallmentsChange(redistributeAcrossSlots(next, freeSlots, newRemaining, roundTo))
        },
        [editedIndices, effectiveLockedCount, installments, frozenLockedSum, total, count, roundTo, onInstallmentsChange, onEditedIndicesChange]
    )

    const redistributeRemaining = useCallback(() => {
        if (freeCount <= 0 || total <= 0) return
        const next = [...installments]
        const freeSlots: number[] = []
        for (let i = effectiveLockedCount; i < count; i++) {
            if (!editedIndices.has(i)) freeSlots.push(i)
        }
        onInstallmentsChange(redistributeAcrossSlots(next, freeSlots, remaining, roundTo))
    }, [freeCount, total, remaining, installments, count, effectiveLockedCount, editedIndices, roundTo, onInstallmentsChange])

    const applyFrontLoaded = useCallback(() => {
        const editableTotal = Math.max(0, total - frozenLockedSum)
        const editableCount = count - effectiveLockedCount
        if (editableCount <= 1 || editableTotal <= 0) return resetToEqual()
        onInstallmentsChange(buildWithLocked(frontLoadedSplit(editableTotal, editableCount, roundTo)))
        onEditedIndicesChange(new Set([effectiveLockedCount]))
    }, [count, total, frozenLockedSum, effectiveLockedCount, roundTo, onInstallmentsChange, onEditedIndicesChange, buildWithLocked, resetToEqual])

    const applyGraduatedDecreasing = useCallback(() => {
        const editableTotal = Math.max(0, total - frozenLockedSum)
        const editableCount = count - effectiveLockedCount
        if (editableCount <= 1 || editableTotal <= 0) return resetToEqual()
        onInstallmentsChange(buildWithLocked(graduatedDecreasingSplit(editableTotal, editableCount, roundTo)))
        onEditedIndicesChange(new Set(Array.from({ length: editableCount }, (_, i) => i + effectiveLockedCount)))
    }, [count, total, frozenLockedSum, effectiveLockedCount, roundTo, onInstallmentsChange, onEditedIndicesChange, buildWithLocked, resetToEqual])

    if (count <= 0) return null

    const editableCount = count - effectiveLockedCount
    const freeformTotal = freeform ? Math.round(installments.reduce((s, a) => s + a, 0) * 100) / 100 : 0
    const filledCount = freeform ? installments.filter((v, i) => i >= effectiveLockedCount && v > 0).length : 0

    const relevantRoundToOptions = ROUND_TO_OPTIONS.filter((o) =>
        isRoundToRelevant(total, count, o)
    )

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Installment Schedule <span className="text-red-500">*</span>
                </label>
                {freeform && freeformTotal > 0 && (
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                        Computed total: <strong>{formatCurrency(freeformTotal)}</strong>
                    </span>
                )}
            </div>

            {freeform && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Enter each installment amount directly. The plan total will be computed from your entries.
                </p>
            )}

            {/* Locked installments info */}
            {effectiveLockedCount > 0 && (
                <div className="flex items-start gap-2 p-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg">
                    <LockClosedIcon className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                        <strong>{effectiveLockedCount}</strong> installment{effectiveLockedCount > 1 ? 's' : ''} locked
                        ({formatCurrency(frozenLockedSum)})
                        {paidOrAppliedCount > 0 && billedOrScheduledCount > 0 ? (
                            <> &mdash; {paidOrAppliedCount} paid/applied, {billedOrScheduledCount} billed/scheduled</>
                        ) : paidOrAppliedCount > 0 ? (
                            <> &mdash; {paidOrAppliedCount} paid/applied</>
                        ) : (
                            <> &mdash; {billedOrScheduledCount} billed/scheduled</>
                        )}
                        . These cannot be modified.
                    </p>
                </div>
            )}

            {/* Quick actions toolbar */}
            {!freeform && editableCount > 0 && (
                <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">
                            Quick fill{effectiveLockedCount > 0 ? ' (editable)' : ''}:
                        </span>
                        <Button size={1} color="slate" onClick={resetToEqual}>
                            <Bars3Icon className="w-3 h-3 mr-1 inline-block" />
                            Equal split
                        </Button>
                        <Button size={1} color="slate" onClick={applyFrontLoaded}>
                            <ArrowTurnRightDownIcon className="w-3 h-3 mr-1 inline-block" />
                            Front-loaded
                        </Button>
                        <Button size={1} color="slate" onClick={applyGraduatedDecreasing}>
                            <ArrowTrendingDownIcon className="w-3 h-3 mr-1 inline-block" />
                            Trending down
                        </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Round to:</span>
                        {relevantRoundToOptions.map((option) => (
                            <button
                                key={option}
                                type="button"
                                onClick={() => setRoundTo(option)}
                                className={
                                    roundTo === option
                                        ? 'inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-200 dark:ring-indigo-700'
                                        : 'inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium border border-transparent text-slate-600 dark:text-slate-50 bg-slate-100 dark:bg-slate-900 hover:bg-slate-200 dark:hover:bg-slate-800 focus:ring-slate-500 focus:outline-none focus:ring-2 focus:ring-offset-2 [&>a]:text-inherit'
                                }
                            >
                                {option === 0.01 ? 'None' : `$${option}`}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Installment grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {installments.map((amount, index) => {
                    const isLocked = index < effectiveLockedCount
                    const lockedStatus = isLocked ? effectiveStatuses[index] : null
                    const isEdited = !isLocked && editedIndices.has(index)
                    const dates = billingDates[index]
                    const isZero = !isLocked && amount <= 0 && (freeform ? freeformTotal > 0 : total > 0)

                    const isPaidOrApplied = lockedStatus === 'Paid' || lockedStatus === 'Applied'
                    const isBilledOrScheduled = lockedStatus === 'Billed' || lockedStatus === 'Scheduled'

                    const inputClass = isLocked
                        ? isPaidOrApplied
                            ? 'opacity-60 bg-green-50 dark:bg-green-900/20 cursor-not-allowed border-green-300 dark:border-green-700'
                            : 'opacity-60 bg-amber-50 dark:bg-amber-900/20 cursor-not-allowed border-amber-300 dark:border-amber-700'
                        : isZero
                            ? 'border-red-300 bg-red-50/50 dark:bg-red-900/20 ring-red-500'
                            : isEdited
                                ? 'border-blue-300 bg-blue-50/50 dark:bg-blue-900/20 ring-blue-500'
                                : ''

                    return (
                        <div key={index} className="space-y-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <label
                                    htmlFor={`installment-${index}`}
                                    className="text-sm font-medium text-slate-700 dark:text-slate-300"
                                >
                                    Installment {index + 1}
                                </label>
                                {isPaidOrApplied && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 ring-1 ring-green-200 dark:ring-green-700">
                                        <CheckCircleIcon className="w-2.5 h-2.5 mr-0.5" />
                                        {lockedStatus}
                                    </span>
                                )}
                                {isBilledOrScheduled && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 ring-1 ring-amber-200 dark:ring-amber-700">
                                        <DocumentTextIcon className="w-2.5 h-2.5 mr-0.5" />
                                        {lockedStatus}
                                    </span>
                                )}
                                {isEdited && (
                                    <>
                                        <PencilIcon className="w-3 h-3 text-blue-500" aria-label="Manually edited" />
                                        <button
                                            type="button"
                                            onClick={() => resetSingleInstallment(index)}
                                            title="Reset this installment"
                                            className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                        >
                                            <ArrowUturnLeftIcon className="w-2.5 h-2.5" />
                                            Reset
                                        </button>
                                    </>
                                )}
                            </div>
                            {dates && (
                                <div className="text-xs text-gray-400 dark:text-gray-500 space-y-0.5">
                                    {dates.bill && (
                                        <p>Est. Bill: {dates.bill.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                                    )}
                                    <p>Est. Due: {dates.due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                                </div>
                            )}
                            <input
                                id={`installment-${index}`}
                                type="number"
                                step="0.01"
                                min="0"
                                value={amount || ''}
                                onChange={(e) => updateSingleInstallment(index, e.target.value)}
                                placeholder="0.00"
                                disabled={isLocked}
                                className={`${getInputStyles({ disabled: isLocked, error: isZero })} ${inputClass}`}
                            />
                            {isZero && (
                                <p className="text-xs text-red-600 dark:text-red-400">Must be greater than $0</p>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Summary panel */}
            {freeform ? (
                <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-md space-y-2">
                    {effectiveLockedCount > 0 && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Locked installments ({effectiveLockedCount}):</span>
                            <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(frozenLockedSum)}</span>
                        </div>
                    )}
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Plan total:</span>
                        <span className={`font-medium ${freeformTotal > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                            {formatCurrency(freeformTotal)}
                        </span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Installments filled:</span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">{filledCount} of {editableCount}</span>
                    </div>
                    {filledCount === editableCount && freeformTotal > 0 && (
                        <p className="text-green-600 dark:text-green-400 text-xs">{'\u2713'} All installments entered</p>
                    )}
                </div>
            ) : (
                <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-md space-y-2">
                    {effectiveLockedCount > 0 && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Locked installments ({effectiveLockedCount}):</span>
                            <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(frozenLockedSum)}</span>
                        </div>
                    )}
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Installment total:</span>
                        <span className={`font-medium ${hasError ? (difference > 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400') : 'text-green-600 dark:text-green-400'}`}>
                            {formatCurrency(installmentsTotal)}
                            {hasError && (
                                <span className="ml-1 text-xs">
                                    ({difference > 0 ? '+' : ''}{difference.toFixed(2)})
                                </span>
                            )}
                        </span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">{totalLabel}:</span>
                        <span className="font-medium dark:text-gray-200">{formatCurrency(total)}</span>
                    </div>
                    {!hasError && total > 0 && (
                        <p className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs">
                            <CheckIcon className="w-3.5 h-3.5 shrink-0" />
                            Totals match
                        </p>
                    )}
                </div>
            )}

            {/* Redistribute remaining */}
            {!freeform && (editedIndices.size > 0 || effectiveLockedCount > 0) && freeCount > 0 && hasError && (
                <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <ViewColumnsIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm text-blue-800 dark:text-blue-300">
                            {effectiveLockedCount > 0 && (
                                <><strong>{effectiveLockedCount}</strong> locked ({formatCurrency(frozenLockedSum)}). </>
                            )}
                            {editedIndices.size > 0 && (
                                <><strong>{editedIndices.size}</strong> edited manually ({formatCurrency(editedOnlyTotal)}). </>
                            )}
                            Remaining <strong>{formatCurrency(remaining)}</strong> can be split across{' '}
                            <strong>{freeCount}</strong> installment{freeCount > 1 ? 's' : ''} ({formatCurrency(remaining / freeCount)} each).
                        </p>
                        <Button
                            size={1}
                            color="slate"
                            onClick={redistributeRemaining}
                            disabled={remaining < 0}
                            className="mt-2"
                        >
                            <ViewColumnsIcon className="w-3 h-3 mr-1 inline-block" />
                            Redistribute remaining
                        </Button>
                        {remaining < 0 && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                Fixed installments already exceed the plan total.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Zero-installment validation error */}
            {hasZeroInstallment && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <ExclamationCircleIcon className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-400">
                        All installments must be greater than $0. Adjust any zero-value installments above.
                    </p>
                </div>
            )}

            {/* Sum mismatch validation error */}
            {!freeform && hasError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <ExclamationCircleIcon className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-400">
                        Installment total ({formatCurrency(installmentsTotal)}) must equal {totalLabel.toLowerCase()} ({formatCurrency(total)}).
                        {freeCount > 0 && remaining >= 0 && <> Use &quot;Redistribute remaining&quot; to auto-balance.</>}
                    </p>
                </div>
            )}

            {editedIndices.size > 0 && !freeform && (
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 ring-1 ring-blue-200 dark:ring-blue-700">
                        <PencilIcon className="w-3 h-3 mr-1" />
                        {editedIndices.size} of {editableCount} manually edited
                    </span>
                    <button
                        type="button"
                        onClick={resetToEqual}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline cursor-pointer"
                    >
                        Clear edits &amp; reset
                    </button>
                </div>
            )}
        </div>
    )
}

export default InstallmentConfigurator
